import { router, usePage } from "@inertiajs/react"
import { useEffect, useLayoutEffect, useRef } from "react"

import {
  analyticsEnabled,
  analyticsSessionId,
  capturePageView,
  identifyAnalyticsUser,
  initializeAnalytics,
  resetAnalyticsUser,
} from "@/lib/posthog"
import type { AnalyticsSharedData, User } from "@/types"

interface AnalyticsProps extends Record<string, unknown> {
  auth?: {
    user?: User | null
  }
  analytics?: AnalyticsSharedData
}

// Direct-entry page events run from child useEffect hooks, so PostHog needs
// to be bootstrapped and identified before passive effects fire in the page.
const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect

export function useAnalytics() {
  const page = usePage<AnalyticsProps>()
  const pageviewUrlRef = useRef<string | null>(null)

  const user = page.props.auth?.user ?? null
  const enabled = page.props.analytics?.enabled === true
  const analyticsProjectToken = page.props.analytics?.project_token
  const analyticsHost = page.props.analytics?.host
  const anonymousDistinctId = page.props.analytics?.anonymous_distinct_id
  const stitchAuthJourney = Boolean(page.props.analytics?.stitch_auth_journey)
  const bootstrapDistinctId =
    !user || stitchAuthJourney ? anonymousDistinctId : undefined

  useBrowserLayoutEffect(() => {
    initializeAnalytics({
      enabled,
      userId: user?.id,
      projectToken: analyticsProjectToken,
      host: analyticsHost,
      bootstrapDistinctId,
    })
  }, [
    enabled,
    user?.id,
    analyticsHost,
    analyticsProjectToken,
    bootstrapDistinctId,
  ])

  useBrowserLayoutEffect(() => {
    if (!analyticsEnabled()) return

    if (user) {
      identifyAnalyticsUser(user)
    } else {
      resetAnalyticsUser()
    }
  }, [user, enabled])

  useEffect(
    () =>
      router.on("before", (event) => {
        if (event.detail.visit.url.origin !== window.location.origin) return
        const sessionId = analyticsSessionId()
        if (sessionId)
          event.detail.visit.headers["X-POSTHOG-SESSION-ID"] = sessionId
      }),
    [],
  )

  useEffect(() => {
    if (!analyticsEnabled() || pageviewUrlRef.current === page.url) return

    pageviewUrlRef.current = page.url

    capturePageView({
      page_path: window.location.pathname,
      page_url: window.location.href,
      is_authenticated: Boolean(user),
    })
  }, [page.url, user, enabled])
}
